import React, { useMemo, useState, useRef, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend } from 'recharts';
import useIsMobile from '../hooks/useIsMobile';
import { getBloqueiosCategoryColor, calculatePercentile } from '../utils/chartUtils';
import SummaryCard from './SummaryCard';
import Filters from './Filters';
import DateRangeFilter from './DateRangeFilter';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';

function BloqueiosChart({ data, allCategories, visibleCategories, setVisibleCategories, plausible }) {
  console.log('🎯 BloqueiosChart RENDERIZANDO!', {
    hasData: !!data,
    dataLength: data?.length,
    hasAllCategories: !!allCategories,
    allCategoriesLength: allCategories?.length,
    hasVisibleCategories: !!visibleCategories,
    visibleCategoriesLength: visibleCategories?.length
  });
  
  const isMobile = useIsMobile();
  const chartAreaRef = useRef(null);
  
  // Debug logs - updated
  console.log('🔍 BloqueiosChart - dados recebidos:', { 
    dataLength: data?.length, 
    allCategoriesLength: allCategories?.length,
    visibleCategoriesLength: visibleCategories?.length,
    sampleData: data?.slice(0, 2)
  });
  
  // Estado para tipo de visualização
  const [viewType, setViewType] = useState('timeline'); // 'timeline', 'duracao', 'categorias'
  
  // Estado para escala temporal
  const [timeScale, setTimeScale] = useState('mensal'); // 'semanal', 'mensal', 'trimestral'
  
  // Opções de escala temporal
  const timeScaleOptions = [
    { key: 'semanal', label: 'Semanal' },
    { key: 'mensal', label: 'Mensal' },
    { key: 'trimestral', label: 'Trimestral' },
  ];
  
  // Estado para detalhes da barra clicada
  const [selectedBarDetails, setSelectedBarDetails] = useState(null);
  
  // Estado para modal de confirmação de exportação
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Estado para modal de configuração de cores
  const [showColorModal, setShowColorModal] = useState(false);
  
  // Estado para cores temporárias (antes de aplicar)
  const [tempColors, setTempColors] = useState({});
  
  // Estado para cores personalizadas (apenas durante a sessão)
  const [customColors, setCustomColors] = useState({});
  
  // Limpar localStorage antigo ao montar o componente
  useEffect(() => {
    localStorage.removeItem('bloqueios-custom-colors');
  }, []);
  
  // Filtro de datas
  const closedDates = useMemo(() => {
    return data.map(item => new Date(item['Data de Conclusão'])).sort((a, b) => a - b);
  }, [data]);
  
  const defaultEnd = closedDates.length ? closedDates[closedDates.length - 1] : new Date();
  const defaultStart = closedDates.length ? new Date(defaultEnd) : new Date();
  if (closedDates.length) {
    // Definir para os últimos 6 meses (180 dias aproximadamente)
    defaultStart.setUTCMonth(defaultEnd.getUTCMonth() - 6);
    // Se a data calculada for anterior à primeira data disponível, usar a primeira data
    const firstDate = closedDates[0];
    if (defaultStart < firstDate) {
      defaultStart.setTime(firstDate.getTime());
    }
  }
  
  const [startDate, setStartDate] = useState(defaultStart.toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(defaultEnd.toISOString().slice(0, 10));

  // Dados processados
  const processedData = useMemo(() => {
    console.log('🔄 Processando dados de bloqueios:', data?.length, 'itens');
    const processed = data.map(item => {
      const criacaoDate = new Date(item['Data de Criação']);
      const conclusaoDate = new Date(item['Data de Conclusão']);
      const duracaoDias = Math.ceil((conclusaoDate - criacaoDate) / (1000 * 60 * 60 * 24));
      
      return {
        ...item,
        criacaoDate,
        conclusaoDate,
        duracaoDias,
        categoria: item['Categoria']?.trim() || 'Padrão'
      };
    });
    console.log('✅ Dados processados:', processed?.length, 'itens', processed?.slice(0, 2));
    return processed;
  }, [data]);

  // Dados filtrados
  const filteredData = useMemo(() => {
    console.log('🔍 Filtrando dados:', {
      processedLength: processedData?.length,
      startDate,
      endDate,
      visibleCategories,
      visibleCategoriesLength: visibleCategories?.length
    });
    const filtered = processedData.filter(item => {
      const d = item.conclusaoDate;
      const dateInRange = d >= new Date(startDate) && d <= new Date(endDate);
      // Se visibleCategories está vazio, mostrar todas as categorias
      const categoryVisible = visibleCategories.length === 0 || visibleCategories.includes(item.categoria);
      
      return dateInRange && categoryVisible;
    });
    console.log('✅ Dados filtrados:', filtered?.length, 'itens');
    return filtered;
  }, [processedData, startDate, endDate, visibleCategories]);

  // Estatísticas gerais
  const stats = useMemo(() => {
    if (!filteredData.length) return { total: 0, media: 0, p50: 0, p85: 0, p95: 0 };
    
    const duracoes = filteredData.map(item => item.duracaoDias);
    const total = filteredData.length;
    const media = Math.round(duracoes.reduce((sum, d) => sum + d, 0) / total);
    
    return {
      total,
      media,
      p50: calculatePercentile(duracoes, 50),
      p85: calculatePercentile(duracoes, 85),
      p95: calculatePercentile(duracoes, 95)
    };
  }, [filteredData]);

  // Dados para gráfico de duração com detalhes dos itens e contagens por categoria
  const duracaoChartData = useMemo(() => {
    if (!filteredData.length || !allCategories?.length) return [];
    
    // Agrupar por faixas de duração com detalhes dos itens e contagens por categoria
    const bins = {};
    filteredData.forEach(item => {
      const duracao = item.duracaoDias;
      let range;
      
      if (duracao <= 1) range = '1 dia';
      else if (duracao <= 3) range = '2-3 dias';
      else if (duracao <= 7) range = '4-7 dias';
      else if (duracao <= 14) range = '8-14 dias';
      else if (duracao <= 30) range = '15-30 dias';
      else range = '30+ dias';
      
      if (!bins[range]) {
        bins[range] = { 
          count: 0, 
          items: [],
          // Inicializar contadores para cada categoria
          ...Object.fromEntries(allCategories.map(cat => [cat, 0]))
        };
      }
      bins[range].count++;
      bins[range][item.categoria]++;
      bins[range].items.push({
        id: item.ID,
        categoria: item.categoria,
        duracaoDias: item.duracaoDias,
        criacaoDate: item.criacaoDate,
        conclusaoDate: item.conclusaoDate,
        dataCriacao: item['Data de Criação'],
        dataConclusao: item['Data de Conclusão']
      });
    });
    
    const order = ['1 dia', '2-3 dias', '4-7 dias', '8-14 dias', '15-30 dias', '30+ dias'];
    return order.map(range => ({
      range,
      count: bins[range]?.count || 0,
      items: bins[range]?.items || [],
      // Incluir contagens por categoria
      ...Object.fromEntries(allCategories.map(cat => [cat, bins[range]?.[cat] || 0]))
    }));
  }, [filteredData, allCategories]);

  // Dados para gráfico de categorias
  const categoriaChartData = useMemo(() => {
    if (!filteredData.length) return [];
    
    const grouped = {};
    filteredData.forEach(item => {
      const categoria = item.categoria;
      if (!grouped[categoria]) {
        grouped[categoria] = { categoria, count: 0, totalDias: 0 };
      }
      grouped[categoria].count++;
      grouped[categoria].totalDias += item.duracaoDias;
    });
    
    return Object.values(grouped).map(item => ({
      ...item,
      mediaDias: Math.round(item.totalDias / item.count)
    }));
  }, [filteredData]);

  // Dados para gráfico de timeline baseado na data de criação
  const timelineChartData = useMemo(() => {
    if (!filteredData.length || !allCategories?.length) return [];
    
    // Função para formatar período baseado na escala
    const formatPeriod = (date, scale) => {
      const d = new Date(date);
      if (scale === 'semanal') {
        // Calcular semana do ano (ISO week)
        const startOfYear = new Date(d.getFullYear(), 0, 1);
        const pastDaysOfYear = (d - startOfYear) / 86400000;
        const weekNumber = Math.ceil((pastDaysOfYear + startOfYear.getDay() + 1) / 7);
        return `${d.getFullYear()}-S${weekNumber.toString().padStart(2, '0')}`;
      } else if (scale === 'mensal') {
        return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`;
      } else if (scale === 'trimestral') {
        const quarter = Math.ceil((d.getMonth() + 1) / 3);
        return `${d.getFullYear()}-T${quarter}`;
      }
      return '';
    };
    
    // Função para formatar período para exibição
    const formatPeriodDisplay = (periodo, scale) => {
      if (scale === 'mensal') {
        const [year, month] = periodo.split('-');
        const monthNames = [
          'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
          'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
        ];
        return `${monthNames[parseInt(month) - 1]}/${year}`;
      } else if (scale === 'trimestral') {
        const [year, quarter] = periodo.split('-T');
        return `${quarter}º Tri/${year}`;
      } else if (scale === 'semanal') {
        const [year, week] = periodo.split('-S');
        return `S${week}/${year}`;
      }
      return periodo; // Fallback
    };
    
    // Agrupar por período e categoria
    const grouped = {};
    filteredData.forEach(item => {
      const periodo = formatPeriod(item['Data de Criação'], timeScale);
      const categoria = item.categoria;
      
      if (!grouped[periodo]) {
        grouped[periodo] = {
          periodo,
          periodoDisplay: formatPeriodDisplay(periodo, timeScale),
          total: 0,
          items: [],
          ...Object.fromEntries(allCategories.map(cat => [cat, 0]))
        };
      }
      
      grouped[periodo].total++;
      grouped[periodo][categoria]++;
      grouped[periodo].items.push({
        id: item.ID,
        categoria: item.categoria,
        duracaoDias: item.duracaoDias,
        dataCriacao: item['Data de Criação'],
        dataConclusao: item['Data de Conclusão']
      });
    });
    
    // Converter para array e ordenar por período
    return Object.values(grouped).sort((a, b) => a.periodo.localeCompare(b.periodo));
  }, [filteredData, allCategories, timeScale]);

  // Função para lidar com clique na barra
  const handleBarClick = (data) => {
    if (data && data.items && data.items.length > 0) {
      setSelectedBarDetails({
        range: data.range,
        count: data.count,
        items: data.items.sort((a, b) => b.duracaoDias - a.duracaoDias) // Ordenar por duração desc
      });
    }
  };

  // Função para fechar detalhes
  const closeDetails = () => {
    setSelectedBarDetails(null);
  };

  // Função de exportação
  const handleExport = () => {
    setShowExportModal(true);
  };

  // Função para confirmar exportação
  const confirmExport = () => {
    setShowExportModal(false);
    
    if (!chartAreaRef.current) return;
    
    if (plausible) {
      plausible.trackExport('png', 'bloqueios', `bloqueios-${viewType}`);
      plausible.trackSection('export-png');
    }
    
    toPng(chartAreaRef.current, { backgroundColor: '#fff' })
      .then((dataUrl) => {
        saveAs(dataUrl, `bloqueios-${viewType}.png`);
      })
      .catch(() => {
        alert('Erro ao exportar imagem');
      });
  };

  // Função para cancelar exportação
  const cancelExport = () => {
    setShowExportModal(false);
  };

  // Função para obter cor atual (temporária, personalizada ou padrão)
  const getCurrentColor = (categoria) => {
    return tempColors[categoria] || customColors[categoria] || getBloqueiosCategoryColor(categoria, allCategories);
  };

  // Função para atualizar cor temporária
  const updateTempColor = (categoria, color) => {
    setTempColors(prev => ({
      ...prev,
      [categoria]: color
    }));
  };

  // Função para resetar cor temporária de uma categoria
  const resetTempColor = (categoria) => {
    if (tempColors[categoria]) {
      // Se há cor temporária, apenas removê-la
      setTempColors(prev => {
        const newColors = { ...prev };
        delete newColors[categoria];
        return newColors;
      });
    } else if (customColors[categoria]) {
      // Se não há cor temporária mas há personalizada, criar temporária como padrão
      const defaultColor = getBloqueiosCategoryColor(categoria, allCategories);
      setTempColors(prev => ({
        ...prev,
        [categoria]: defaultColor
      }));
    }
  };

  // Função para resetar todas as cores temporárias
  const resetAllTempColors = () => {
    setTempColors({});
  };

  const applyColorChanges = () => {
    // Mesclar cores temporárias com as personalizadas existentes
    const updatedColors = { ...customColors, ...tempColors };
    
    // Atualizar estado das cores personalizadas (apenas na sessão atual)
    setCustomColors(updatedColors);
    
    // Limpar cores temporárias e fechar modal
    setTempColors({});
    setShowColorModal(false);
    
    console.log('✅ Cores aplicadas (sessão atual):', updatedColors);
  };

  const resetToDefaults = () => {
    // Resetar estados para cores padrão
    setCustomColors({});
    setTempColors({});
    
    console.log('🔄 Todas as cores redefinidas para padrão');
  };

  // Renderizar gráfico baseado no tipo de visualização
  const renderChart = () => {
    if (viewType === 'duracao') {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-md font-medium text-gray-800 mb-3 text-center">Distribuição por Faixas de Duração</h4>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={duracaoChartData} margin={{ top: 10, right: 10, left: 30, bottom: 10 }}>
              <XAxis 
                dataKey="range" 
                tick={{ fontSize: isMobile ? 10 : 12, fill: '#374151' }}
                textAnchor="middle"
                height={40}
              />
              <YAxis 
                tick={{ fontSize: isMobile ? 10 : 13, fill: '#374151', fontWeight: 500 }} 
                label={{ 
                  value: 'Quantidade de Bloqueios', 
                  angle: -90, 
                  position: 'left',
                  fontSize: isMobile ? 10 : 13,
                  fill: '#374151',
                  fontWeight: 600,
                  dx: 0,
                  dy: -50
                }}
                allowDecimals={false}
              />
              <Tooltip 
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const total = payload.reduce((sum, entry) => sum + entry.value, 0);
                    return (
                      <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                        <p className="font-semibold">{label}</p>
                        <p className="text-blue-600 mb-2">Total: {total} bloqueios</p>
                        {payload.map((entry, index) => (
                          entry.value > 0 && (
                            <p key={index} className="text-sm" style={{ color: entry.color }}>
                              {entry.dataKey}: {entry.value}
                            </p>
                          )
                        ))}
                        <p className="text-gray-500 text-sm mt-2">Clique para ver detalhes</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              {allCategories?.map((categoria, index) => (
                <Bar 
                  key={categoria}
                  dataKey={categoria} 
                  stackId="a"
                  fill={getCurrentColor(categoria)}
                  cursor="pointer"
                  onClick={handleBarClick}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }
    
    if (viewType === 'categorias') {
      return (
        <div className="space-y-6">
          
          {/* Container dos gráficos - Layout vertical */}
          <div className="space-y-6">
            {/* Gráfico de Pizza - Quantidade por Categoria */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-md font-medium text-gray-800 mb-3 text-center">Quantidade por Categoria</h4>
              <div className="flex flex-col lg:flex-row items-center justify-center gap-6">
                {/* Gráfico */}
                <div className="flex-shrink-0">
                  <ResponsiveContainer width={isMobile ? 300 : 400} height={240}>
                    <PieChart>
                      <Pie
                        data={categoriaChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ categoria, count, percent, cx, cy, midAngle, innerRadius, outerRadius }) => {
                          const RADIAN = Math.PI / 180;
                          const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                          const x = cx + radius * Math.cos(-midAngle * RADIAN);
                          const y = cy + radius * Math.sin(-midAngle * RADIAN);
                          
                          // Mostrar apenas percentual
                          const percentage = (percent * 100).toFixed(0);
                          
                          return (
                            <text 
                              x={x} 
                              y={y} 
                              fill="#ffffff" 
                              textAnchor={x > cx ? 'start' : 'end'} 
                              dominantBaseline="central"
                              fontSize={isMobile ? "11" : "12"}
                              fontWeight="600"
                              stroke="#000000"
                              strokeWidth="0.3"
                              paintOrder="stroke fill"
                            >
                              {percentage}%
                            </text>
                          );
                        }}
                        outerRadius={isMobile ? 85 : 100}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {categoriaChartData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={getCurrentColor(entry.categoria)} 
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                                <p className="font-semibold">{data.categoria}</p>
                                <p className="text-blue-600">Bloqueios: {data.count}</p>
                                <p className="text-sm text-gray-500">
                                  {((data.count / categoriaChartData.reduce((sum, item) => sum + item.count, 0)) * 100).toFixed(0)}%
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Legenda à direita */}
                <div className="flex flex-col gap-3 lg:max-w-xs">
                  {categoriaChartData.map((entry, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded flex-shrink-0"
                        style={{ backgroundColor: getCurrentColor(entry.categoria) }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {entry.categoria}
                        </p>
                        <p className="text-xs text-gray-600">
                          {entry.count} bloqueios ({((entry.count / categoriaChartData.reduce((sum, item) => sum + item.count, 0)) * 100).toFixed(0)}%)
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Gráfico de Barras - Duração Média por Categoria */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-md font-medium text-gray-800 mb-3 text-center">Duração Média por Categoria</h4>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart 
                  data={categoriaChartData} 
                  margin={{ top: 1, right: 20, left: 80, bottom: isMobile ? 30 : 15 }}
                >
                  <XAxis 
                    dataKey="categoria" 
                    tick={{ fontSize: isMobile ? 10 : 12, fill: '#374151' }}
                    textAnchor={isMobile ? "end" : "middle"}
                    height={isMobile ? 30 : 15}
                    interval={0}
                    angle={isMobile ? -45 : 0}
                  />
                  <YAxis 
                    tick={{ fontSize: isMobile ? 10 : 13, fill: '#374151', fontWeight: 500 }} 
                    label={{ 
                      value: 'Duração Média (dias)', 
                      angle: -90, 
                      position: 'left',
                      fontSize: isMobile ? 10 : 13,
                      fill: '#374151',
                      fontWeight: 600,
                      dx: 0,
                      dy: -50
                    }}
                    allowDecimals={false}
                  />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                            <p className="font-semibold">{label}</p>
                            <p className="text-blue-600">Duração média: {data.mediaDias} dias</p>
                            <p className="text-gray-600">Total de bloqueios: {data.count}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar 
                    dataKey="mediaDias" 
                    fill="#3B82F6"
                    radius={[4, 4, 0, 0]}
                  >
                    {categoriaChartData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={getCurrentColor(entry.categoria)} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      );
    }
    
    if (viewType === 'timeline') {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-md font-medium text-gray-800 mb-3 text-center">Bloqueios Criados por Período</h4>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={timelineChartData} margin={{ top: 10, right: 10, left: 30, bottom: 10 }}>
              <XAxis 
                dataKey="periodoDisplay" 
                tick={{ fontSize: isMobile ? 9 : 11, fill: '#374151' }}
                textAnchor="middle"
                height={40}
              />
              <YAxis 
                tick={{ fontSize: isMobile ? 10 : 13, fill: '#374151', fontWeight: 500 }} 
                label={{ 
                  value: 'Bloqueios Criados', 
                  angle: -90, 
                  position: 'left',
                  fontSize: isMobile ? 10 : 13,
                  fill: '#374151',
                  fontWeight: 600,
                  dx: 0,
                  dy: -40
                }}
                allowDecimals={false}
              />
              <Tooltip 
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const total = payload.reduce((sum, entry) => sum + entry.value, 0);
                    return (
                      <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                        <p className="font-semibold">{label}</p>
                        <p className="text-blue-600 mb-2">Total: {total} bloqueios criados</p>
                        {payload.map((entry, index) => (
                          entry.value > 0 && (
                            <p key={index} className="text-sm" style={{ color: entry.color }}>
                              {entry.dataKey}: {entry.value}
                            </p>
                          )
                        ))}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              {allCategories?.map((categoria, index) => (
                <Bar 
                  key={categoria}
                  dataKey={categoria} 
                  stackId="timeline"
                  fill={getCurrentColor(categoria)}
                  cursor="pointer"
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }

    return <div className="text-center py-8 text-gray-500">Tipo de visualização não implementado</div>;
  };

  const viewOptions = [
    { key: 'timeline', label: '📅 Timeline' },
    { key: 'duracao', label: '📊 Duração' },
    { key: 'categorias', label: '🏷️ Categorias' },
  ];

  const summaryCards = [
    <SummaryCard key="total" label="Total de bloqueios" value={stats.total} colorClass="text-gray-800 text-lg" />,
    <SummaryCard key="media" label="Duração média" value={`${stats.media} dias`} colorClass="text-red-700 text-lg" />,
    <SummaryCard key="p50" label="P50 (Mediana)" value={`${stats.p50} dias`} colorClass="text-blue-700 text-lg" />,
    <SummaryCard key="p95" label="P95" value={`${stats.p95} dias`} colorClass="text-orange-700 text-lg" />,
  ];

  return (
    <>
      <div className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-1 sm:p-6 mt-8 mb-16 px-0 sm:px-2 flex flex-col items-stretch relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 px-4 sm:px-0">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-800">Análise de Bloqueios</h3>
          </div>
          <div className="flex items-center gap-4">
            {/* Seletor de tipo de visualização */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              {viewOptions.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setViewType(opt.key)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewType === opt.key 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
              
              {/* Botão de exportar */}
              <button
                onClick={handleExport}
                className="px-4 py-2 rounded-md text-sm font-medium transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                title="Exportar gráfico como imagem"
              >
                📸 Exportar
              </button>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="mb-4 px-4 sm:px-0">
          <div className="flex items-center gap-3 mb-3">
            <Filters
              allTypes={allCategories}
              visibleTypes={visibleCategories}
              setVisibleTypes={setVisibleCategories}
              visiblePercentiles={{}}
              togglePercentile={() => {}}
              typeLabel="Categorias"
              getTypeColor={getCurrentColor}
            />
            
            {/* Botão de configuração de cores */}
            {allCategories.length > 0 && (
              <button
                onClick={() => setShowColorModal(true)}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200 hover:border-gray-300"
                title="Personalizar cores das categorias"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                </svg>
                <span className="text-sm font-medium hidden sm:inline">Cores</span>
              </button>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 items-center mt-4">
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              minDate={closedDates.length ? closedDates[0].toISOString().slice(0, 10) : ''}
              maxDate={closedDates.length ? closedDates[closedDates.length - 1].toISOString().slice(0, 10) : ''}
            />
            
            {/* Filtro de escala temporal (visível apenas para timeline) */}
            {viewType === 'timeline' && (
              <div className="flex flex-row gap-2 w-full sm:w-auto justify-start sm:justify-end">
                {timeScaleOptions.map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setTimeScale(opt.key)}
                    className={`px-3 py-1 rounded-full border text-sm font-medium transition-all ${
                      timeScale === opt.key 
                        ? 'bg-blue-600 text-white border-blue-700 scale-105' 
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-blue-50'
                    }`}
                    type="button"
                    style={{ minWidth: 90 }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Área do gráfico */}
        <div ref={chartAreaRef} className="px-4 sm:px-0" style={{ background: '#fff', borderRadius: 8, padding: 0 }}>
          {filteredData.length > 0 ? renderChart() : (
            <div className="text-center py-8 text-gray-500">
              <p>Nenhum dado de bloqueios encontrado para o período selecionado.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de detalhes dos bloqueios */}
      {selectedBarDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            {/* Header do modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Bloqueios - {selectedBarDetails.range}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedBarDetails.count} bloqueio{selectedBarDetails.count !== 1 ? 's' : ''} encontrado{selectedBarDetails.count !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={closeDetails}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Fechar"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Conteúdo do modal */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
              <div className="space-y-4">
                {selectedBarDetails.items.map((item, index) => (
                  <div 
                    key={item.id} 
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-gray-900">#{item.id}</span>
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{ 
                            backgroundColor: getCurrentColor(item.categoria) + '20',
                            color: getCurrentColor(item.categoria)
                          }}
                        >
                          {item.categoria}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-red-600">
                          {item.duracaoDias} dia{item.duracaoDias !== 1 ? 's' : ''}
                        </div>
                        <div className="text-xs text-gray-500">duração</div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Data de Criação:</span>
                        <div className="text-gray-600 mt-1">{item.dataCriacao}</div>
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-gray-700">Data de Conclusão:</span>
                        <div className="text-gray-600 mt-1">{item.dataConclusao}</div>
                      </div>
                    </div>
                    
                    {/* Barra de progresso visual da duração */}
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Início</span>
                        <span>Fim</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full"
                          style={{ 
                            width: `${Math.min(100, (item.duracaoDias / Math.max(...selectedBarDetails.items.map(i => i.duracaoDias))) * 100)}%`
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer do modal */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                Total: {selectedBarDetails.count} bloqueio{selectedBarDetails.count !== 1 ? 's' : ''} • 
                Duração média: {Math.round(selectedBarDetails.items.reduce((sum, item) => sum + item.duracaoDias, 0) / selectedBarDetails.items.length)} dias
              </div>
              <button
                onClick={closeDetails}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação de exportação */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-md w-full">
            <div className="flex items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Exportar Gráfico</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Deseja fazer o download do gráfico de Bloqueios como imagem PNG?
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelExport}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmExport}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
              >
                Fazer Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de configuração de cores */}
      {showColorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
            {/* Header do modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  🎨 Personalizar Cores
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Escolha cores personalizadas para cada categoria
                </p>
                {Object.keys(tempColors).length > 0 && (
                  <div className="mt-2 flex items-center gap-1 text-sm text-amber-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.99-.833-2.76 0L4.054 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <span>{Object.keys(tempColors).length} cor{Object.keys(tempColors).length !== 1 ? 'es' : ''} não salva{Object.keys(tempColors).length !== 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowColorModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Fechar"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Conteúdo do modal */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
              <div className="space-y-4">
                {allCategories.map((categoria, index) => (
                  <div key={categoria} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-6 h-6 rounded border-2 border-gray-300"
                        style={{ backgroundColor: getCurrentColor(categoria) }}
                      />
                      <span className="font-medium text-gray-800">{categoria}</span>
                      {(tempColors[categoria] || customColors[categoria]) && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          Personalizada
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={getCurrentColor(categoria)}
                        onChange={(e) => updateTempColor(categoria, e.target.value)}
                        className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                        title={`Alterar cor de ${categoria}`}
                      />
                      <button
                        onClick={() => resetTempColor(categoria)}
                        className="text-gray-500 hover:text-gray-700 text-sm p-1 hover:bg-gray-200 rounded transition-colors"
                        title="Restaurar cor padrão"
                        disabled={!tempColors[categoria] && !customColors[categoria]}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer do modal */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={resetToDefaults}
                className="text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                🔄 Resetar Todas
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setTempColors({});
                    setShowColorModal(false);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={applyColorChanges}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50"
                  disabled={Object.keys(tempColors).length === 0}
                >
                  Aplicar {Object.keys(tempColors).length > 0 && `(${Object.keys(tempColors).length})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cards de resumo */}
      {filteredData.length > 0 && (
        <div className="flex flex-row flex-nowrap w-full items-center gap-2 mt-6 max-w-4xl mx-auto pb-2 px-1 snap-x min-w-0 overflow-x-auto whitespace-nowrap">
          {summaryCards}
        </div>
      )}
    </>
  );
}

export default BloqueiosChart;